







































































































































































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { State, Action } from "vuex-class";
import logger from "@/configs/logger";
import { firestore, collections } from "@/configs/firebase";
import { User, Roles } from "@/types";
import moment from "moment";
import { objectify } from "@/helpers";

@Component
export default class LeadProduct extends Vue {
  @Prop() lead!: any;
  @State("user") user!: User;

  groups: any = {};
  selectedGroup: string = "";
  activeProductType: string = "";
  selectedProducts: any = {};
  productOptions: any = [];

  effectiveDate: string = "";
  effectiveDateMenu = false;

  postDate: string = "";
  postDateMenu: boolean = false;

  agentEmails: string[] = [];

  failReason: string = "";
  showFailReasonInputError: boolean = false;
  showEnrollmentFeeError: boolean = false;
  showPostDateError: boolean = false;

  unsubscribeSelectedProducts!: Function;

  @Watch("postDate")
  onPostDateChange(val: string, prev: string) {
    if (val && val !== prev) {
      this.selectedProducts[this.activeProductType] = {
        ...this.selectedProducts[this.activeProductType],
        post_date: val
      };
      this.saveProduct();
    }
  }

  @Watch("effectiveDate")
  onEffectiveDateChange(val: string, prev: string) {
    if (val && val !== prev) {
      this.selectedProducts[this.activeProductType] = {
        ...this.selectedProducts[this.activeProductType],
        effective_date: val
      };
      this.saveProduct();
    }
  }

  async getProducts() {
    const snap = await collections.productOptions
      .where("is_active", "==", true)
      .get();
    this.productOptions = snap.docs.map(item => ({
      id: item.id,
      ...item.data()
    }));
  }

  async created() {
    logger("log", "lead id:", this.lead.id);
    await this.getProducts();
    this.activeProductType = this.productTypes[0];
    this.syncSelectedProducts();
    if (this.isAdminOrVerification) {
      this.fetchAllAgentsEmails();
    }
  }

  beforeDestroy(): void {
    this.unsubscribeSelectedProducts && this.unsubscribeSelectedProducts();
  }

  get canViewProducts() {
    return this.lead && this.lead.state_abbr;
  }

  get isEditable(): boolean {
    if (this.user.roles.includes("admin")) {
      return true;
    }
    if (this.activeProduct.status === "failed") {
      return true;
    }
    return !this.activeProduct.status;
  }

  get enableAction(): boolean {
    if (this.user.roles.includes("admin")) {
      return true;
    }
    if (this.activeProduct.status === "failed") {
      return true;
    }
    return this.isAdminOrVerification && !this.activeProduct.status;
  }

  get productGroups() {
    const groups: any = {};
    this.productOptions.forEach((product: any) => {
      groups[product.type] = groups[product.type] || [];
      if (!groups[product.type].includes(product.group)) {
        groups[product.type].push(product.group);
      }
    });
    logger("log", "product groups\n", groups);
    return groups;
  }

  get productTypes(): any[] {
    const types = new Set();
    this.productOptions.forEach((item: any) => types.add(item.type));
    return Array.from(types);
  }

  get activeProductOptions(): any[] {
    const activeType = this.activeProductType || this.productTypes[0];
    return this.getProductsByType(activeType)
      .filter(
        item => item.group === this.selectedGroup || this.activeProduct.group
      )
      .filter(item => {
        if (!Array.isArray(item.state_whitelist)) {
          return true;
        }
        if (this.lead.state_abbr) {
          return item.state_whitelist.includes(this.lead.state_abbr);
        }
        return false;
      });
  }

  get activeProduct(): any {
    if (!this.activeProductType) return {};
    const active = this.selectedProducts[this.activeProductType];
    return active ? active : {};
  }

  get saleCanceledReasons(): string[] {
    return ["card declined", "verification failed", "cannot contact customer"];
  }

  get isAdminOrVerification(): boolean {
    return (
      this.user.roles.includes("admin") ||
      this.user.roles.includes("verification")
    );
  }

  get exceededMaxEnrollmentFee() {
    return this.totalEnrollmentFee > 170;
  }

  get totalCommisions() {
    return Object.values(this.selectedProducts).reduce((s: number, c: any) => {
      return s + Number((c && c.commission) || 0);
    }, 0);
  }

  get totalEnrollmentFee(): number {
    return Object.values(this.selectedProducts).reduce((s: number, c: any) => {
      return s + Number((c && c.enrollment_fee) || 0);
    }, 0);
  }

  get totalPremium(): number {
    return Object.values(this.selectedProducts).reduce((s: number, c: any) => {
      return s + Number((c && c.premium) || 0);
    }, 0);
  }

  get dueMonthly(): number {
    return Math.round(this.totalPremium * 100) / 100;
  }

  get dueNow(): number {
    return Math.round((this.totalEnrollmentFee + this.dueMonthly) * 100) / 100;
  }

  allowedEffectiveDates(val: string): boolean {
    if (
      this.activeProduct.group === "niia_health" &&
      this.activeProduct.name.toLowerCase().includes("share")
    ) {
      const day = parseInt(val.split("-")[2], 10);
      return day === 1 || day === 15;
    }
    return true;
  }

  async fetchAllAgentsEmails(): Promise<void> {
    try {
      const snapshot = await collections.user.limit(80).get();
      this.agentEmails = snapshot.docs.map(doc => {
        if (doc.data().email) {
          return doc.data().email;
        }
      });
    } catch (err) {
      logger("error", "Error fetching agent emails", err);
    }
  }

  async syncSelectedProducts(): Promise<void> {
    try {
      this.unsubscribeSelectedProducts && this.unsubscribeSelectedProducts();
      this.unsubscribeSelectedProducts = collections.lead
        .doc(this.lead.id)
        .collection("selected_products")
        .onSnapshot(snapshot => {
          const products: any = {};
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            products[data.type] = {
              ...data,
              closer_email: this.getDealAgentsEmail("closer", data),
              fronter_email: this.getDealAgentsEmail("fronter", data)
            };
          });
          this.selectedProducts = products;
          this.setDefaultValues();
          logger("log", "selected products:", this.selectedProducts);
        });
    } catch (err) {
      logger("error", "selected products fetch error", err);
    }
  }

  getEnrollmentFeeCommission(activeProduct: any) {
    if (activeProduct.enrollment_fee_commission) {
      return (
        activeProduct.enrollment_fee_commission[activeProduct.enrollment_fee] ||
        "0.00"
      );
    }
    return "0.00";
  }

  getDealAgentsEmail(role: string, product: any): string {
    const defaultEmails = (role: string) =>
      objectify(this.lead.assigned_agent_email_by_role)[role] || "";

    switch (role) {
      case "closer":
        return product.closer_email || defaultEmails("closer");
      case "fronter":
        return product.fronter_email || defaultEmails("fronter");
      default:
        return "";
    }
  }

  getStatusColor(type: string): string {
    const product = this.getSelectedProduct(type);
    switch (product.status) {
      case "sale":
        return "green lighten-2";
      case "failed":
        return "deep-orange lighten-1";
      case "post_date":
        return "cyan darken-3";
      default:
        return product && product.name ? "deep-purple" : "blue-grey lighten-2";
    }
  }

  getSelectedProduct(type: string) {
    return this.selectedProducts[type] ? this.selectedProducts[type] : {};
  }

  getProductsByType(type: string): any[] {
    return this.productOptions.filter((item: any) => item.type === type);
  }

  setActiveType(type: string, toggle: Function): void {
    this.activeProductType = type;
    this.setDefaultValues();
    const group = this.productGroups[type];
    if (Array.isArray(group) && group.length === 1) {
      this.selectedGroup = group[0];
    } else {
      this.selectedGroup = "";
    }
    logger("log", this.selectedProducts);
    toggle();
  }

  setDefaultValues(): void {
    // default values for props accessed via v-model
    // firestore does not accept undefined
    const product = objectify(this.activeProduct);
    this.effectiveDate = product.effective_date || "";
    this.postDate = product.post_date || "";
    this.failReason = product.failed_reason || "";
  }

  setEnrollmentFee(value: string): void {
    this.showEnrollmentFeeError = false;
    this.selectedProducts[this.activeProductType] = {
      ...this.selectedProducts[this.activeProductType],
      enrollment_fee: value
    };
    logger("log", this.selectedProducts);
  }

  setAgents(value: string, type: string): void {
    this.selectedProducts[this.activeProductType] = {
      ...this.selectedProducts[this.activeProductType],
      [type]: value
    };
  }

  selectGroup(val: string): void {
    this.selectedGroup = val;
  }

  async selectProduct(name: string): Promise<void> {
    const product = this.productOptions.find((item: any) => item.name === name);
    this.selectedProducts = {
      ...this.selectedProducts,
      [this.activeProductType]: product
    };
    logger("log", product);
    logger("log", this.selectedProducts);
    await this.saveProduct();
  }

  async setPostDate(): Promise<any> {
    try {
      if (!this.postDate) {
        this.showPostDateError = true;
        return;
      }
      this.showPostDateError = false;
      this.selectedProducts[this.activeProductType] = {
        ...this.selectedProducts[this.activeProductType],
        post_date: this.postDate,
        post_date_ts: firestore.Timestamp.now(),
        status: "post_date"
      };
      await this.saveProduct();
      this.$toasted.success("Product is now set to post date.").goAway(2000);
    } catch (err) {
      logger("error", "set post date error", err);
      this.$toasted.error("Failed to set post date.").goAway(2000);
    }
  }

  async setFailed(): Promise<void> {
    try {
      if (!this.failReason) {
        this.showFailReasonInputError = true;
      } else {
        this.showFailReasonInputError = false;
        this.selectedProducts[this.activeProductType] = {
          ...this.selectedProducts[this.activeProductType],
          failed_reason: this.failReason,
          failed_ts: firestore.Timestamp.now(),
          status: "failed"
        };
        await this.saveProduct();
        this.$toasted.success("Product is now set to failed.").goAway(2000);
      }
    } catch (err) {
      logger("error", "set to failed", err);
      this.$toasted.error("Failed to set product to failed.").goAway(2000);
    }
  }

  async setSale(): Promise<void> {
    try {
      if (
        !this.activeProduct.enrollment_fee ||
        !this.activeProduct.effective_date
      ) {
        this.$toasted.error("Enrollment fee and effective date required.");
        return;
      }
      this.selectedProducts[this.activeProductType] = {
        ...this.selectedProducts[this.activeProductType],
        status: "sale",
        sale_ts: firestore.Timestamp.now()
      };
      await this.saveProduct();
      this.$toasted.success("Product is now set to sale.").goAway(2000);
    } catch (err) {
      logger("error", "set to sale", err);
      this.$toasted.error("Failed to set product to sale.").goAway(2000);
    }
  }

  async deleteProduct(): Promise<void> {
    try {
      await collections.lead
        .doc(this.lead.id)
        .collection("selected_products")
        .doc(this.activeProductType)
        .delete();
      this.selectedProducts[this.activeProductType] = {};
      this.$toasted.success("Product deleted.").goAway(2000);
    } catch (err) {
      logger("error", "product delete error", err);
      this.$toasted.error("Product delete failed").goAway(2000);
    }
  }

  async saveProduct(): Promise<void> {
    try {
      if (this.exceededMaxEnrollmentFee) {
        logger("warn", "exceeded max. enrollment fee.");
        return;
      }
      if (!this.lead.id) {
        logger("warn", "lead id not available");
        return;
      }
      await collections.lead
        .doc(this.lead.id)
        .collection("selected_products")
        .doc(this.activeProductType)
        .set(this.selectedProducts[this.activeProductType], { merge: true });
    } catch (err) {
      logger("error", "product save error", err);
      this.$toasted.error("Failed to save product").goAway(2000);
    }
  }

  async changeProductStatus(status: string): Promise<void> {
    if (this.activeProduct.status) {
      const confirmed = window.confirm(
        "Are you sure you want to make changes to an already copleted product?"
      );
      if (!confirmed) return;
    }
    if (status === "post_date") {
      await this.setPostDate();
    }
    if (status === "sale") {
      await this.setSale();
    }
    if (status === "failed") {
      await this.setFailed();
    }
  }

  formatTitle(title: string) {
    return title.replace(/_/, " ").toUpperCase();
  }
}
